<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['retry']);

const { t } = useI18n();
const { dateTime } = useLocalizedTime();

// message_type: incoming (0) is the user's own message, outgoing (1) is the support team's reply
const isOwnMessage = computed(() => props.message.message_type === 0);
const senderName = computed(() => props.message.sender?.name || '');
// Support Contacts are named "Icaro (Acme Inc, conta #1, usuário #1)" (spec 112 v4) —
// Avatar.vue derives initials by splitting on whitespace, so the parenthesized suffix
// would otherwise leak into the fallback initials (e.g. "I(" instead of "I").
const avatarName = computed(() => senderName.value.split(' (')[0]);
const senderThumbnail = computed(() => props.message.sender?.thumbnail || '');
const readableTime = computed(() => dateTime(props.message.created_at));
const senderTooltip = computed(() =>
  t('SUPPORT_CHAT.SENT_BY', { name: avatarName.value })
);
</script>

<template>
  <div
    class="flex items-end gap-2"
    :class="isOwnMessage ? 'justify-end' : 'justify-start'"
  >
    <Avatar
      v-if="!isOwnMessage"
      v-tooltip.top="senderTooltip"
      :src="senderThumbnail"
      :name="avatarName"
      :size="24"
    />

    <div
      class="flex flex-col gap-1 max-w-lg"
      :class="isOwnMessage ? 'items-end' : 'items-start'"
    >
      <span v-if="!isOwnMessage" class="text-xs text-n-slate-11 px-1">
        {{ senderName }}
      </span>

      <div
        class="text-sm min-w-0 px-4 py-3"
        :class="[
          isOwnMessage
            ? 'bg-n-solid-blue text-n-slate-12 rounded-xl rounded-br-sm'
            : 'bg-n-slate-4 text-n-slate-12 rounded-xl rounded-bl-sm',
          {
            'opacity-60': message.isSending,
            'outline outline-1 outline-n-ruby-9': message.isFailed,
          },
        ]"
      >
        <p v-if="message.content" class="whitespace-pre-wrap break-words">
          {{ message.content }}
        </p>

        <div
          v-if="message.attachments?.length"
          class="flex flex-col gap-2"
          :class="{ 'mt-2': message.content }"
        >
          <a
            v-for="attachment in message.attachments"
            :key="attachment.id"
            :href="attachment.data_url"
            target="_blank"
            rel="noopener noreferrer"
            class="underline break-all"
          >
            {{ attachment.extension || 'anexo' }}
          </a>
        </div>
      </div>

      <button
        v-if="message.isFailed"
        type="button"
        class="flex items-center gap-1 text-xs text-n-ruby-9 px-1 disabled:opacity-50"
        :disabled="disabled"
        @click="$emit('retry')"
      >
        <Icon icon="i-lucide-refresh-cw" class="size-3" />
        {{ t('SUPPORT_CHAT.SEND_FAILED_RETRY') }}
      </button>
      <span v-else-if="message.isSending" class="text-xs text-n-slate-10 px-1">
        {{ t('SUPPORT_CHAT.SENDING') }}
      </span>
      <span v-else class="text-xs text-n-slate-10 px-1">{{
        readableTime
      }}</span>
    </div>

    <Avatar
      v-if="isOwnMessage"
      v-tooltip.top-end="senderTooltip"
      :src="senderThumbnail"
      :name="avatarName"
      :size="24"
    />
  </div>
</template>
